import { CountryCode, CurrencyCode, ProductCodeKey, ProductVariantPrice, Variant } from '@dayetopia/library';
import {
  PAD_STACK_PRODUCT_IDS,
  COUNTRY_SPECIFIC_PRICES,
  PRICES_WITHOUT_DISCOUNT,
  CURRENCY_EUR,
  CURRENCY_WITH_SYMBOL_AFTER,
  CURRENCY_BGN
} from '@constants';
import { CartItem, CartItemType, CurrencyObj, PadSizeType, PriceComplex, PriceSimple, PriceText } from '@contracts';
import { convertBGNToEUR, convertEURToBGN, shouldShowDualCurrency } from './currencyConversion';
import { getProductPrices } from './prices';

/**
 * Retrieves the price as a string based on currency and whether the service is prepaid. Handles undefined prices.
 * @param {PriceComplex | undefined} price - The price information, which can be undefined, a string, or an array of currency-amount pairs.
 * @param {CurrencyObj} currency - The currency object containing the letter code to match in the price object.
 * @param {boolean} [isPrepaid=false] - Optional. Specifies whether the price calculation should consider the item as prepaid.
 * @returns {PriceSimple} The price as a string. If the price is prepaid or undefined, returns '0.00'.
 */
export const getPriceString = (price: PriceComplex | undefined, currency: CurrencyObj, isPrepaid?: boolean): PriceSimple => {
  const priceByCurrency = Array.isArray(price) ? price?.find((p) => p.currency === currency.letterCode)?.amount : price;

  return determineDisplayPrice(priceByCurrency, isPrepaid);
};

/**
 * Converts the price into an array of [dollars, cents] based on currency and whether the service is prepaid.
 * @param {PriceComplex | undefined} price - The price information, which can be undefined, a string, or an array of currency-amount pairs.
 * @param {CurrencyObj} currency - The currency object to filter the price by.
 * @param {boolean} [isPrepaid=false] - Optional. Determines if the price should be considered as prepaid.
 * @returns {[string, string]} An array where the first element is dollars and the second is cents. If prepaid or undefined, returns ['0', '00'].
 */
export const getPriceArray = (price: PriceComplex | undefined, currency: CurrencyObj, isPrepaid?: boolean): [string, string] => {
  const priceString = getPriceString(price, currency, isPrepaid);
  const [dollars, cents = '00'] = priceString.split('.');

  return [dollars, cents];
};

/**
 * Determines the display format of the price, considering if it is prepaid.
 * @param {PriceSimple} [price] - Optional. The price as a simple string, which can be undefined.
 * @param {boolean} [isPrepaid=false] - Optional. If true, or if the price is undefined, the function returns '0.00', indicating no charge or non-applicability.
 * @returns {PriceSimple} A string representing the formatted price, '0.00' if prepaid or undefined, or the original price if not.
 */
export const determineDisplayPrice = (price?: PriceSimple, isPrepaid?: boolean): PriceSimple => {
  if (isPrepaid || !price) {
    return '0.00';
  }

  return price;
};

/**
 * Compares two price arrays to determine if they are equal.
 * @param {string[]} price1 - First price array consisting of [dollars, cents].
 * @param {string[]} price2 - Second price array consisting of [dollars, cents].
 * @returns {boolean} True if both price arrays are identical, false otherwise.
 */
export const arePriceArraysEqual = (price1: [string, string], price2: [string, string]): boolean => {
  return price1[0] === price2[0] && price1[1] === price2[1];
};

/**
 * Formats a price based on the country and currency provided.
 * @param {PriceComplex} price - The price to be formatted, as an object with properties for dollars and cents.
 * @param {CurrencyObj} currency - The currency to be used, as an object with properties for symbol and code.
 * @param {CountryCode} country - The country code to determine the format of the price.
 * @returns {string} The formatted price string. If the country is GB or US, the currency symbol precedes the price. Otherwise, the currency symbol follows the price.
 */
export const formatPriceBasedOnCountry = (price: PriceComplex, currency: CurrencyObj, country: CountryCode): string => {
  const priceStr = getPriceString(price, currency);
  if (country === CountryCode.GB || country === CountryCode.US) {
    return `${currency.symbol}${priceStr}`;
  }
  return `${priceStr}${currency.symbol}`;
};

/**
 * Validates if a price value is valid for formatting.
 * @param {unknown} price - The price value to validate
 * @returns {boolean} True if the price is valid for formatting
 */
export const isValidPrice = (price: unknown): price is number => {
  return price !== null && price !== undefined && typeof price === 'number' && Number.isFinite(price);
};

/**
 * Resolves the price for a product based on country-specific overrides.
 * Falls back to base amount if no override exists for the country.
 * @param priceData - The price data object containing amount and optional overrides
 * @param country - The country code to check for override
 * @returns The resolved price amount
 */
export function resolvePrice(priceData: ProductVariantPrice, country: CountryCode): number {
  const override = priceData.amountOverride?.[country];
  if (override !== undefined && override !== null) {
    return override;
  }
  return priceData.amount;
}

export const roundNumber = (number: number) => Math.round(number * 10) / 10;

export const getNewProductPrice = async (currency: CurrencyObj, country: CountryCode, productId: string, subscriptionPlan?: string) => {
  const pricesData = await getProductPrices();

  if (pricesData && Array.isArray(pricesData)) {
    const product = pricesData.find((item: { id: string }) => item.id === productId);

    if (product) {
      // Calculate VAT percentage
      const vatPercent = product.vat ? product.vat[country] ?? 0 : 0;

      let priceItem;

      if (product.pricePerPlan && subscriptionPlan) {
        const priceForPlan = product.pricePerPlan.find((plan: { planType: string }) => plan.planType === subscriptionPlan);

        priceItem = priceForPlan?.price.find((item: { amount: number; currency: string }) => item.currency === currency.letterCode);
      } else {
        priceItem = product.price?.find((item: { amount: number; currency: string }) => item.currency === currency.letterCode);
      }

      if (priceItem) {
        // Use country-specific override if available, otherwise use base amount
        const baseAmount = resolvePrice(priceItem as ProductVariantPrice, country);
        const vatAmount = baseAmount * (vatPercent / 100);
        return Number((baseAmount + vatAmount).toFixed(2));
      }
    }
  }

  return -1;
};

export const getDiscountedPrice = (originalPrice: number, discountPercentage: number): number => {
  const itemPrice = originalPrice - (originalPrice * discountPercentage) / 100;
  return Math.round(itemPrice * 20) / 20;
};

export async function getProductPrice(productId: string, currency: CurrencyObj, country: CountryCode) {
  const pricesData = await getProductPrices();

  if (pricesData && productId) {
    const product = pricesData.find((itm) => itm.id === productId);
    const firstVariant = product?.variants?.[0];
    const productPrice = product?.price || firstVariant?.price;

    if (productPrice) {
      const vatPercent = product?.vat[country] ?? 0;
      const price = productPrice.find((p) => p.currency === currency.letterCode);

      if (price) {
        // Use country-specific override if available, otherwise use base amount
        const baseAmount = resolvePrice(price as ProductVariantPrice, country);
        const vatAmount = baseAmount * (vatPercent / 100);
        const finalPrice = baseAmount + vatAmount;
        return { price: Number(finalPrice.toFixed(2)) };
      }
    }
  }
  return { price: 0 };
}

export const fragmentPrice = (price: number) => price.toFixed(2).split('.');

export const getPoundsFromPrice = (price: number) => fragmentPrice(price)[0];

export const getCoinsFromPrice = (price: number) => fragmentPrice(price)[1];

export async function getPadStackPrices(newVariants: Variant, currency: CurrencyObj, country: CountryCode) {
  const plKey = PAD_STACK_PRODUCT_IDS[PadSizeType.LINER];
  const prKey = PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR];
  const pnKey = PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER];
  const pnpKey = PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS];
  const linerPadStackPrice = (await getProductPrice(plKey, currency, country))?.price || 0;
  const regularPadStackPrice = (await getProductPrice(prKey, currency, country))?.price || 0;
  const nightPadStackPrice = (await getProductPrice(pnKey, currency, country))?.price || 0;
  const nightPlusPadStackPrice = (await getProductPrice(pnpKey, currency, country))?.price || 0;

  return {
    [PAD_STACK_PRODUCT_IDS[PadSizeType.LINER]]: linerPadStackPrice * (newVariants[plKey] || 0),
    [PAD_STACK_PRODUCT_IDS[PadSizeType.REGULAR]]: regularPadStackPrice * (newVariants[prKey] || 0),
    [PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER]]: nightPadStackPrice * (newVariants[pnKey] || 0),
    [PAD_STACK_PRODUCT_IDS[PadSizeType.SUPER_PLUS]]: nightPlusPadStackPrice * (newVariants[pnpKey] || 0)
  };
}

/**
 * The complete price formatting function that handles all scenarios.
 * Supports null/undefined values, negative numbers, dual currency, and flexible output formats.
 * This is the single source of truth for all price formatting in the application.
 *
 * @param {number | null | undefined} price - The price value (handles null/undefined).
 * @param {CountryCode} country - The country code used to decide symbol positioning.
 * @param {CurrencyObj} currency - The currency object containing symbol and letterCode information.
 * @param {(key: string) => string} t - The translation function.
 * @param {boolean} [variablePrice] - Optional. Indicates if the price is variable.
 * @param {boolean} [startingFrom] - Optional. Indicates if the display should include a 'starting-from' prefix.
 * @param {boolean} [alwaysShowCents] - Optional. Always show .00 even when cents are zero.
 * @param {boolean} [includeSymbol] - Optional. Whether to include the currency symbol in the output.
 * @returns {PriceText} The formatted price components with fullString property.
 */
export const getPriceText = (
  price: number | null | undefined,
  country: CountryCode,
  currency: CurrencyObj,
  t: (key: string) => string,
  variablePrice?: boolean,
  startingFrom?: boolean,
  alwaysShowCents = false,
  includeSymbol = true,
  skipSecondCurrency = false
): PriceText => {
  // Handle null/undefined values - return empty components object
  if (!isValidPrice(price)) {
    return {
      fullString: '',
      prefix: '',
      main: '',
      sup: '',
      suffix: ''
    };
  }

  // Handle negative prices (edge case, but might occur in refunds/discounts)
  const absolutePrice = Math.abs(price);
  const isNegative = price < 0;
  const safePrice = absolutePrice;
  let priceString = safePrice.toFixed(2);

  const [dollars, cents] = priceString.split('.');
  const isSymbolBefore = !CURRENCY_WITH_SYMBOL_AFTER.includes(currency.letterCode);

  let startText = '';

  if (variablePrice || startingFrom) {
    startText = `${t('components.starting-from')} `;
  }

  let prefix = '';
  const main = dollars;
  const isZeroCents = cents === '00';
  const sup = alwaysShowCents || !isZeroCents ? cents : '';
  let suffix = '';

  if (isSymbolBefore) {
    // Currency symbol comes before the price
    prefix = startText + (includeSymbol ? currency.symbol : ''); // No space needed when symbol is before
  } else {
    // Currency symbol comes after the price
    prefix = startText;
    suffix = includeSymbol ? currency.symbol : ''; // Add space conditionally
  }

  const secondPrice = skipSecondCurrency ? undefined : getSecondCurrencyPrice(safePrice, country, currency, alwaysShowCents, includeSymbol);

  // Omit .00 when cents are zero (unless alwaysShowCents is true)
  let fullString = prefix + main + (alwaysShowCents || !isZeroCents ? `.${sup}` : '') + suffix;

  if (secondPrice) {
    fullString = `${fullString}/${secondPrice.fullString}`;
  }

  // Handle negative sign for the full string
  if (isNegative) {
    fullString = `-${fullString}`;
    priceString = `-${priceString}`;

    // Fix dual currency to also be negative
    if (secondPrice) {
      fullString = fullString.replace(/\/(.+)/, '/-$1');
    }
  }

  // Return components object
  return {
    fullString,
    prefix: isNegative ? `-${prefix}` : prefix,
    main,
    sup,
    suffix,
    priceString,
    secondPrice
  };
};

/**
 * Gets the price in the second currency for dual currency display.
 * For Bulgaria with EUR: shows BGN equivalent as secondary currency
 * For legacy BGN users: shows EUR equivalent as secondary currency
 * @param {number} safePrice - The price to be formatted.
 * @param {CountryCode} country - The country code to determine dual currency behavior.
 * @param {CurrencyObj} currency - The currency object containing symbol and letterCode information.
 * @param {boolean} [alwaysShowCents] - Optional. Whether to always show cents even when .00.
 * @param {boolean} [includeSymbol] - Optional. Whether to include the currency symbol in the output.
 * @returns {PriceText | undefined} The formatted price in the second currency, or undefined if not applicable.
 */
export function getSecondCurrencyPrice(
  safePrice: number,
  country: CountryCode,
  currency: CurrencyObj,
  alwaysShowCents = false,
  includeSymbol = true
): PriceText | undefined {
  // Bulgaria with EUR: show BGN equivalent as secondary currency
  if (shouldShowDualCurrency(country) && currency.letterCode === CurrencyCode.EUR) {
    const bgnAmount = convertEURToBGN(safePrice);
    // Pass skipSecondCurrency = true to prevent infinite recursion
    const bgnPriceText = getPriceText(bgnAmount, CountryCode.BG, CURRENCY_BGN, (t) => t, false, false, alwaysShowCents, includeSymbol, true);

    return bgnPriceText;
  }

  // Legacy: show EUR equivalent whenever currency is BGN
  if (currency.letterCode === CurrencyCode.BGN) {
    const eurAmount = convertBGNToEUR(safePrice);
    // Pass skipSecondCurrency = true to prevent infinite recursion
    const eurPriceText = getPriceText(eurAmount, CountryCode.DE, CURRENCY_EUR, (t) => t, false, false, alwaysShowCents, includeSymbol, true);

    return eurPriceText;
  }

  return undefined;
}

/**
 * Gets the discounted price for a product based on country-specific pricing rules.
 * First checks for country-specific prices, then falls back to default prices without discount.
 *
 * @param {ProductCodeKey} productCode - The product code to get the price for (e.g., ProductCode.hpv_advanced)
 * @param {CountryCode} [countryCode] - Optional country code to get country-specific pricing (e.g., CountryCode.IE)
 * @returns {number | null} The discounted price for the product, or null if no price is found
 *
 * @example
 * // Get default price for HPV Advanced test
 * const price = getDiscountedProductPrice(ProductCode.hpv_advanced); // Returns 188
 *
 * // Get country-specific price for Ireland
 * const iePrice = getDiscountedProductPrice(ProductCode.hpv_advanced, CountryCode.IE); // Returns 229.98
 *
 * // Get price for non-existent product
 * const noPrice = getDiscountedProductPrice('invalid_product' as ProductCode); // Returns null
 */
export function getDiscountedProductPrice(productCode: ProductCodeKey, countryCode?: CountryCode): number | null {
  // Convert enum value to string key
  const productKey = productCode as keyof typeof PRICES_WITHOUT_DISCOUNT;

  // First check for country-specific price if a country code is provided
  if (countryCode && COUNTRY_SPECIFIC_PRICES[countryCode]) {
    const countryPrices = COUNTRY_SPECIFIC_PRICES[countryCode];
    const countryPrice = countryPrices?.[productKey];
    if (countryPrice !== undefined) {
      return countryPrice;
    }
  }

  // If no country-specific price found or no country code provided, use default price
  const defaultPrice = PRICES_WITHOUT_DISCOUNT[productKey];
  return defaultPrice !== undefined ? defaultPrice : null;
}

export async function getTamponStackPrices(newVariants: Variant, currency: CurrencyObj, country: CountryCode) {
  const cbdRegularKey = ProductCodeKey.TamponBox9CR;
  const cbdSuperKey = ProductCodeKey.TamponBox9CS;
  const nudeRegularKey = ProductCodeKey.TamponBox18NR;
  const nudeSuperKey = ProductCodeKey.TamponBox18NS;
  const mixedRegularKey = ProductCodeKey.TamponBox9NR9CR;
  const mixedSuperKey = ProductCodeKey.TamponBox9NS9CS;
  const cbdRegularPrice = (await getProductPrice(cbdRegularKey, currency, country))?.price || 0;
  const cbdSuperPrice = (await getProductPrice(cbdSuperKey, currency, country))?.price || 0;
  const nudeRegularPrice = (await getProductPrice(nudeRegularKey, currency, country))?.price || 0;
  const nudeSuperPrice = (await getProductPrice(nudeSuperKey, currency, country))?.price || 0;
  const mixedRegularPrice = (await getProductPrice(mixedRegularKey, currency, country))?.price || 0;
  const mixedSuperPrice = (await getProductPrice(mixedSuperKey, currency, country))?.price || 0;

  return {
    [ProductCodeKey.TamponBox9CR]: cbdRegularPrice * (newVariants[cbdRegularKey] || 0),
    [ProductCodeKey.TamponBox9CS]: cbdSuperPrice * (newVariants[cbdSuperKey] || 0),
    [ProductCodeKey.TamponBox18NR]: nudeRegularPrice * (newVariants[nudeRegularKey] || 0),
    [ProductCodeKey.TamponBox18NS]: nudeSuperPrice * (newVariants[nudeSuperKey] || 0),
    [ProductCodeKey.TamponBox9NR9CR]: mixedRegularPrice * (newVariants[mixedRegularKey] || 0),
    [ProductCodeKey.TamponBox9NS9CS]: mixedSuperPrice * (newVariants[mixedSuperKey] || 0)
  };
}

/**
 * Calculates total cart price excluding donation items.
 * Excludes donation items since donations should not contribute to free shipping.
 *
 * @param {CartItem[]} cartItems - The cart items to calculate the total price from.
 * @returns {number} The total price of non-donation items for free shipping purposes.
 */
export const calculateTotalPrice = (cartItems: CartItem[]): number => {
  return cartItems.filter((item) => item.type !== CartItemType.Donation).reduce((total, item) => total + item.price * item.quantity, 0);
};

/**
 * Calculates the subtotal of product items, excluding donation items.
 * This is useful for calculating the donation amount based on product prices only.
 *
 * @param {CartItem[]} cartItems - The cart items to calculate the product subtotal from.
 * @returns {number} The total price of non-donation items.
 */
export const calculateProductSubtotal = (cartItems: CartItem[]): number => {
  return cartItems.filter((item) => item.type !== CartItemType.Donation).reduce((total, item) => total + item.price * item.quantity, 0);
};
